import { createRef, useState, useEffect, Children } from 'react';
import { TabContextProvider, useTabContext } from './TabContext';
import { Flex } from '../..';
import { InternalTabItem } from './Tab';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const Container = styled.div.withConfig({
  displayName: "Tabs__Container",
  componentId: "sc-17qrlum-0"
})(({
  $variant,
  theme: _theme
}) => {
  const theme = _theme;
  const defaultBorder = $variant === 'default' ? {
    borderBlockEndWidth: theme.borderWidth['100'],
    borderBlockEndColor: theme.color['border-primary-default'],
    borderBlockEndStyle: 'solid'
  } : {};
  return Object.assign({}, defaultBorder, {
    marginBlockEnd: `-${theme.borderWidth['100']}`
  });
});
const InternalTabs = ({
  children,
  flexGrow,
  onSelectedChange,
  refs,
  selectedId,
  variant = 'default'
}) => {
  const {
    selected,
    setSelected,
    tabIds,
    disabledTabs
  } = useTabContext();
  const controlled = typeof selectedId === 'number' || typeof selectedId === 'string';
  useEffect(() => {
    if (controlled && selectedId !== selected) {
      setSelected(selectedId);
    }
  }, [controlled, selected, selectedId, setSelected]);
  const [focused, setFocused] = useState(null);
  const alternateBorder = {
    borderBlockEndWidth: '100',
    borderBlockEndColor: 'border-primary-default',
    borderBlockEndStyle: 'solid'
  };
  const selectTab = tabId => {
    const tabIndex = tabIds.indexOf(tabId);
    const tab = refs[tabIndex].current;
    if (tab) {
      tab.focus();
      if (!controlled && !disabledTabs[tabId]) {
        setSelected(tabId);
      }
      if (onSelectedChange && !disabledTabs[tabId]) {
        onSelectedChange(tabId);
      }
    }
  };
  const wrap = direction => {
    let index = tabIds.indexOf(typeof focused === 'number' || typeof focused === 'string' ? focused : selected);
    if (direction === 'next') {
      index++;
      if (index === tabIds.length) {
        index = 0;
      }
    } else {
      index--;
      if (index === -1) {
        index = tabIds.length - 1;
      }
    }
    return tabIds[index];
  };
  const handleKeyDown = event => {
    let flag = true;
    switch (event.key) {
      case 'ArrowLeft':
        selectTab(wrap('previous'));
        break;
      case 'ArrowRight':
        selectTab(wrap('next'));
        break;
      case 'Home':
        if (!disabledTabs[0]) {
          selectTab(tabIds[0]);
        }
        break;
      case 'End':
        if (!disabledTabs[tabIds.length - 1]) {
          selectTab(tabIds[tabIds.length - 1]);
        }
        break;
      default:
        flag = false;
        break;
    }
    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  };
  const handleSelectedChange = tabId => {
    if (onSelectedChange) {
      onSelectedChange(tabId);
    }
    selectTab(tabId);
  };
  const childrenArr = Children.toArray(children);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Container, {
      $variant: variant,
      children: /*#__PURE__*/_jsxs(Flex, {
        role: "tablist",
        flexDirection: "row",
        justifyContent: flexGrow ? 'space-evenly' : 'start',
        children: [childrenArr && childrenArr.map(child => {
          return /*#__PURE__*/_jsx(InternalTabItem, Object.assign({
            flexGrow: flexGrow,
            onKeyDown: handleKeyDown,
            onFocus: id => setFocused(id),
            onSelectedChange: handleSelectedChange,
            refs: refs,
            variant: variant
          }, child.props), child.props.id);
        }), variant !== 'default' && flexGrow !== 1 && /*#__PURE__*/_jsx(Flex, Object.assign({
          flexGrow: 1
        }, alternateBorder))]
      })
    }), childrenArr.map(child => {
      return child.props.id === selected ? child : null;
    })]
  });
};
export const Tabs = props => {
  const childrenArr = Children.toArray(props.children);
  const refs = childrenArr.reduce((acc, _, index) => {
    acc[index] = /*#__PURE__*/createRef();
    return acc;
  }, {});
  return /*#__PURE__*/_jsx(TabContextProvider, {
    defaultSelected: props.defaultSelected ? props.defaultSelected : childrenArr[0].props.id,
    tabIds: childrenArr.map(child => child.props.id),
    disabledTabs: Object.fromEntries(childrenArr.map(child => [child.props.id, child.props.disabled || false])),
    children: /*#__PURE__*/_jsx(InternalTabs, Object.assign({
      refs: refs
    }, props))
  });
};